import React, { useState, useEffect } from 'react';
import { useLocation, useNavigate } from 'react-router-dom';
import {
  Box, Paper, Typography, TextField, Button, Alert, CircularProgress,
  InputAdornment, IconButton, LinearProgress,
} from '@mui/material';
import LockOutlinedIcon from '@mui/icons-material/LockOutlined';
import VisibilityIcon from '@mui/icons-material/Visibility';
import VisibilityOffIcon from '@mui/icons-material/VisibilityOff';
import { confirmarRecuperarPassword } from '../services/authApi';

// Política de password: mínimo 8 caracteres, al menos 1 mayúscula, 1 minúscula,
// 1 dígito y 1 carácter no alfanumérico (Identity default)
function evaluarPassword(pwd) {
  const checks = {
    length:    pwd.length >= 8,
    upper:     /[A-Z]/.test(pwd),
    lower:     /[a-z]/.test(pwd),
    digit:     /[0-9]/.test(pwd),
    nonAlpha:  /[^A-Za-z0-9]/.test(pwd),
  };
  const score = Object.values(checks).filter(Boolean).length;
  return { checks, score, valid: score === 5 };
}

export default function RestablecerPassword() {
  const location = useLocation();
  const navigate = useNavigate();

  // Token y email vienen en la URL: /restablecer-password?token=XYZ&email=ABC
  const params = new URLSearchParams(location.search);
  const token  = params.get('token') || '';
  const email  = params.get('email') || '';

  const [password1, setPassword1] = useState('');
  const [password2, setPassword2] = useState('');
  const [showPass,  setShowPass]  = useState(false);
  const [loading,   setLoading]   = useState(false);
  const [ok,        setOk]        = useState(false);
  const [error,     setError]     = useState('');

  const eval1 = evaluarPassword(password1);

  // Validación inicial del link
  useEffect(() => {
    if (!token || !email) {
      setError('Enlace inválido. Solicita uno nuevo desde "Recuperar contraseña".');
    }
  }, [token, email]);

  const handleSubmit = async (e) => {
    e.preventDefault();
    setError('');
    if (!eval1.valid) {
      setError('La contraseña no cumple con todos los requisitos.');
      return;
    }
    if (password1 !== password2) {
      setError('Las contraseñas no coinciden.');
      return;
    }
    setLoading(true);
    try {
      await confirmarRecuperarPassword(email, token, password1);
      setOk(true);
      setTimeout(() => navigate('/login'), 3000);
    } catch (err) {
      const errores = err?.data?.errores;
      if (Array.isArray(errores) && errores.length) {
        setError(errores.join(' · '));
      } else {
        setError(err?.data?.mensaje || 'No se pudo restablecer la contraseña. El enlace puede haber expirado.');
      }
    } finally {
      setLoading(false);
    }
  };

  return (
    <Box sx={{
      minHeight:'100vh',
      background:'linear-gradient(135deg,#0a1f12 0%,#1a4d2e 50%,#0a1f12 100%)',
      display:'flex', alignItems:'center', justifyContent:'center', p:2,
    }}>
      <Box sx={{ width:'100%', maxWidth:430 }}>
        <Paper elevation={24} sx={{ borderRadius:3, overflow:'hidden' }}>
          <Box sx={{
            background:'linear-gradient(135deg,#1a4d2e 0%,#2e7d32 60%,#43a047 100%)',
            color:'#fff', textAlign:'center', py:3.5, px:3,
          }}>
            <Typography variant="h5" sx={{ fontWeight:700, letterSpacing:'0.02em' }}>
              Nueva contraseña
            </Typography>
            <Typography variant="caption" sx={{ color:'rgba(255,255,255,0.85)', display:'block', mt:0.5 }}>
              FENAGRO APL · Alianza Solidaria
            </Typography>
          </Box>
          <Box sx={{ height:4, background:'linear-gradient(90deg,#c8a84b,#f0d070,#c8a84b)' }}/>

          <Box component="form" onSubmit={handleSubmit} sx={{ px:4, py:3.5 }}>
            {ok ? (
              <Box sx={{ textAlign:'center', py:2 }}>
                <Box sx={{
                  width:64, height:64, borderRadius:'50%',
                  background:'#e8f5e9', color:'#2e7d32',
                  display:'inline-flex', alignItems:'center', justifyContent:'center',
                  fontSize:36, fontWeight:700, mb:2,
                }}>
                  ✓
                </Box>
                <Typography sx={{ fontWeight:700, fontSize:'1.1rem', color:'#1a3a1a', mb:1 }}>
                  Contraseña actualizada
                </Typography>
                <Typography sx={{ color:'#555', fontSize:'0.9rem', mb:2 }}>
                  Te redirigiremos al inicio de sesión en 3 segundos…
                </Typography>
                <LinearProgress sx={{ borderRadius:2 }}/>
              </Box>
            ) : (
              <>
                <Typography sx={{ color:'#555', fontSize:'0.92rem', mb:0.5 }}>
                  Crea una nueva contraseña para:
                </Typography>
                <Typography sx={{ fontSize:'0.92rem', fontWeight:700, color:'#1a3a1a', mb:2.5, fontFamily:'monospace' }}>
                  {email || '—'}
                </Typography>

                {error && <Alert severity="error" sx={{ mb:2 }}>{error}</Alert>}

                <TextField
                  fullWidth label="Nueva contraseña"
                  type={showPass ? 'text' : 'password'}
                  value={password1}
                  onChange={e => setPassword1(e.target.value)}
                  disabled={loading || !token || !email}
                  autoFocus
                  InputProps={{
                    startAdornment: <InputAdornment position="start"><LockOutlinedIcon sx={{ color:'#7d8a7d' }}/></InputAdornment>,
                    endAdornment: (
                      <InputAdornment position="end">
                        <IconButton onClick={() => setShowPass(s => !s)} edge="end" size="small">
                          {showPass ? <VisibilityOffIcon/> : <VisibilityIcon/>}
                        </IconButton>
                      </InputAdornment>
                    ),
                  }}
                  sx={{ mb:1 }}
                />

                {/* Indicadores de seguridad */}
                {password1.length > 0 && (
                  <Box sx={{ mb:2, p:1.5, background:'#f8fdf8', borderRadius:1, border:'1px solid #c8e6c9' }}>
                    <LinearProgress
                      variant="determinate"
                      value={(eval1.score / 5) * 100}
                      sx={{
                        height:6, borderRadius:1, mb:1.2,
                        background:'#e0e0e0',
                        '& .MuiLinearProgress-bar': {
                          background: eval1.score < 3 ? '#d32f2f' : eval1.score < 5 ? '#ed6c02' : '#2e7d32',
                        },
                      }}
                    />
                    <Box sx={{ display:'grid', gridTemplateColumns:'1fr 1fr', gap:'4px 12px', fontSize:'0.72rem' }}>
                      <Check ok={eval1.checks.length}   label="8+ caracteres"/>
                      <Check ok={eval1.checks.upper}    label="Una mayúscula"/>
                      <Check ok={eval1.checks.lower}    label="Una minúscula"/>
                      <Check ok={eval1.checks.digit}    label="Un número"/>
                      <Check ok={eval1.checks.nonAlpha} label="Un símbolo (! @ # $…)"/>
                    </Box>
                  </Box>
                )}

                <TextField
                  fullWidth label="Confirmar contraseña"
                  type={showPass ? 'text' : 'password'}
                  value={password2}
                  onChange={e => setPassword2(e.target.value)}
                  disabled={loading || !token || !email}
                  error={password2.length > 0 && password1 !== password2}
                  helperText={password2.length > 0 && password1 !== password2 ? 'No coincide con la anterior' : ' '}
                  InputProps={{
                    startAdornment: <InputAdornment position="start"><LockOutlinedIcon sx={{ color:'#7d8a7d' }}/></InputAdornment>,
                  }}
                  sx={{ mb:2 }}
                />

                <Button
                  fullWidth type="submit" variant="contained"
                  disabled={loading || !eval1.valid || password1 !== password2 || !token || !email}
                  size="large"
                  endIcon={loading && <CircularProgress size={18} color="inherit" />}
                  sx={{
                    py:1.4, borderRadius:2, fontWeight:700, letterSpacing:'0.05em',
                    background:'linear-gradient(135deg,#1a4d2e,#43a047)',
                    '&:hover':{ background:'linear-gradient(135deg,#0e2a18,#2e7d32)' },
                  }}>
                  {loading ? 'Guardando…' : 'Restablecer contraseña'}
                </Button>

                <Box sx={{ textAlign:'center', mt:2 }}>
                  <a href="/login"
                    style={{ color:'#666', textDecoration:'none', fontSize:'0.85rem' }}>
                    Cancelar
                  </a>
                </Box>
              </>
            )}
          </Box>
        </Paper>

        <Typography variant="caption" sx={{ display:'block', textAlign:'center', color:'rgba(255,255,255,0.3)', mt:2 }}>
          FENAGRO APL · NIT 901026305-2
        </Typography>
      </Box>
    </Box>
  );
}

function Check({ ok, label }) {
  return (
    <Box sx={{
      display:'flex', alignItems:'center', gap:0.5,
      color: ok ? '#2e7d32' : '#999',
      fontWeight: ok ? 600 : 400,
    }}>
      <span style={{ fontSize:'0.85rem' }}>{ok ? '✓' : '○'}</span>
      <span>{label}</span>
    </Box>
  );
}
